/** Two reads beside the factor library - **gate 20**.
 *
 * - `GET /api/v1/factor-sets/market`: which factor sets price **this workspace's** sites, found
 *   server-side with the call the engine prices a site with. The library screen lists those sets
 *   first. The SPA holds no table from a jurisdiction to a set: that would be a second home for a
 *   fact the engine already owns (and the walkthrough of 2026-11-01 found the library listing the
 *   UK set first for a Californian workspace).
 * - `GET /api/v1/factor-sets/compare?from=&to=`: ADR-0120 option B. Two editions of one market's
 *   set, row by row. **Both values, never a difference**: nothing here subtracts two published
 *   figures, so nothing here can round one.
 *
 * Both are views. Neither chooses which edition prices a figure, and neither has a mutation.
 *
 * A body that is not the shape this file was written for throws; it is never an empty answer. An
 * empty `priced_with` is a real state (a workspace in a jurisdiction no set prices) and is returned
 * as itself.
 */

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type FactorMarket = components["schemas"]["FactorLibraryMarketOut"];
export type FactorComparison = components["schemas"]["FactorLibraryComparisonOut"];
export type FactorComparedRow = components["schemas"]["FactorLibraryComparedFactorOut"];

const DECIMAL = /^-?\d+(\.\d+)?$/;
const NOT_THE_MARKET =
  "The workspace's market came back in a shape this screen does not recognise, so the library is shown in EmitIQ's own order.";
const NOT_THE_COMPARISON =
  "The comparison came back in a shape this screen does not recognise, so nothing is shown rather than something wrong.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringOrNull(value: unknown): boolean {
  return value === null || typeof value === "string";
}

function decimalOrNull(value: unknown): boolean {
  return value === null || (typeof value === "string" && DECIMAL.test(value));
}

export function parseFactorMarket(body: unknown): FactorMarket {
  if (
    !isRecord(body) ||
    !["sites", "legal_entities", "deployment_default"].includes(String(body.basis)) ||
    !Array.isArray(body.priced_with) ||
    !Array.isArray(body.unpriced_jurisdictions) ||
    !body.unpriced_jurisdictions.every((entry) => typeof entry === "string")
  ) {
    throw new Error(NOT_THE_MARKET);
  }
  for (const entry of body.priced_with) {
    if (
      !isRecord(entry) ||
      typeof entry.factor_set_key !== "string" ||
      !stringOrNull(entry.jurisdiction) ||
      typeof entry.count !== "number"
    ) {
      throw new Error(NOT_THE_MARKET);
    }
  }
  return body as unknown as FactorMarket;
}

export function parseFactorComparison(body: unknown): FactorComparison {
  if (
    !isRecord(body) ||
    typeof body.from_key !== "string" ||
    typeof body.to_key !== "string" ||
    typeof body.jurisdiction !== "string" ||
    ![body.same, body.changed, body.only_in_from, body.only_in_to].every(
      (count) => typeof count === "number",
    ) ||
    !Array.isArray(body.rows)
  ) {
    throw new Error(NOT_THE_COMPARISON);
  }
  for (const row of body.rows) {
    if (
      !isRecord(row) ||
      !["category", "fuel_or_carrier", "region", "unit_in"].every((key) => typeof row[key] === "string") ||
      !["changed", "only_in_from", "only_in_to"].includes(String(row.outcome)) ||
      !Array.isArray(row.changes) ||
      // Rule 5: a value that is a JSON number means the server stopped sending strings.
      !decimalOrNull(row.from_value) ||
      !decimalOrNull(row.to_value) ||
      !stringOrNull(row.from_unit_out) ||
      !stringOrNull(row.to_unit_out) ||
      !stringOrNull(row.from_external_id) ||
      !stringOrNull(row.to_external_id)
    ) {
      throw new Error(NOT_THE_COMPARISON);
    }
  }
  return body as unknown as FactorComparison;
}

export const factorEditionKeys = {
  market: () => ["factor-library", "market"] as const,
  compare: (from: string, to: string) => ["factor-library", "compare", from, to] as const,
};

async function read(url: string, failure: string): Promise<unknown> {
  const response = await apiFetch(url);
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) throw refusalFrom(response, body, failure);
  return body;
}

/** Which sets price this workspace. A failure here is not a failure of the library: the screen
 * lists the sets in the server's order and says why. */
export function useFactorMarket() {
  return useQuery({
    queryKey: factorEditionKeys.market(),
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<FactorMarket> =>
      parseFactorMarket(await read("/api/v1/factor-sets/market", "The workspace's market could not be read.")),
  });
}

/** One comparison, fetched only when `enabled` (the reader opened it). */
export function useFactorComparison(from: string, to: string, enabled: boolean) {
  return useQuery({
    queryKey: factorEditionKeys.compare(from, to),
    enabled,
    retry: false,
    staleTime: 0,
    queryFn: async (): Promise<FactorComparison> => {
      const query = new URLSearchParams({ from, to });
      return parseFactorComparison(
        await read(`/api/v1/factor-sets/compare?${query.toString()}`, "The comparison could not be read."),
      );
    },
  });
}
